import { useContactContext } from "@/contexts/contact/hooks";
import { useCallback } from "react";
/**
 * A custom React hook that returns a function to normalize name fields in a contact form so cookies/autofill cant leak wrong fields
 * e.g. if fullName is used, firstName and lastName should be empty if before it was prefilled by the values from cookies/autofill.

 *
 * This hook handles two different form scenarios:
 * - Full name form: When enabled, it trims the `fullName` field and clears `firstName` and `lastName`
 * - First/Last name form: When enabled, it clears the `fullName` field while preserving `firstName` and `lastName`
 *
 * @returns A memoized callback function that takes contact form data and returns normalized contact form data
 * with appropriate name fields set based on the form configuration.
 *
 * @example
 * ```typescript
 * const normalizeNameFields = useNormalizeNameFields();
 * const normalizedData = normalizeNameFields({ fullName: "  John Doe  ", firstName: "John", lastName: "Doe" });
 * // If isFullNameForm is true: { fullName: "John Doe", firstName: "", lastName: "" }
 * // If isFullNameForm is false: { fullName: "", firstName: "John", lastName: "Doe" }
 * ```
 */
export const useNormalizeNameFields = () => {
    const { isFullNameForm } = useContactContext();
    return useCallback((data) => {
        var _a;
        if (isFullNameForm) {
            const fullName = ((_a = data.fullName) !== null && _a !== void 0 ? _a : "").trim();
            return Object.assign(Object.assign({}, data), { fullName, firstName: "", lastName: "" });
        }
        return Object.assign(Object.assign({}, data), { fullName: "" });
    }, [isFullNameForm]);
};
